import type { RefObject } from 'react';
import { useContext, useEffect } from 'react';
import type ReCAPTCHA from 'react-google-recaptcha';

import { MessagingContext } from '@sorare/wallet-shared';
import type { ForgotPassword } from '@sorare/wallet-shared';

import { useAuthContext } from '#src/contexts/auth/index.ts';
import { useConnectionContext } from '#src/contexts/connection/index.tsx';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useSnackNotificationContext } from '#src/contexts/snackNotification/index.ts';
import { useWalletContext } from '#src/contexts/wallet/index.ts';
import { useWalletDrawerContext } from '#src/contexts/walletDrawer/index.tsx';
import { useIsMobileApp } from '#src/hooks/useIsMobileApp.ts';
import { sendSafeError } from '#src/lib/error.ts';

export const useForgotPassword = () => {
  const { registerHandler } = useContext(MessagingContext)!;
  const { createResetPasswordRequest } = useAuthContext();
  const { currentUser } = useCurrentUserContext();
  const { showNotification } = useSnackNotificationContext();
  const {
    setPasswordForgotten,
    passwordForgotten,
    recaptchaRef: connectionRecaptcha,
  } = useConnectionContext();
  const { logOut } = useWalletContext();
  const {
    hideWallet,
    hideDrawer,
    recaptchaRef: walletDrawerRecaptcha,
  } = useWalletDrawerContext();
  const { isFlutterApp, postMessage } = useIsMobileApp();

  useEffect(
    () =>
      registerHandler<ForgotPassword>(
        'forgotPassword',
        async ({ email, remainOpened }) => {
          const processRecaptchaRef = async (
            reCAPTCHA: RefObject<ReCAPTCHA | null>['current']
          ) => {
            reCAPTCHA?.reset();
            const recaptchaToken = await reCAPTCHA?.executeAsync();

            if (!recaptchaToken) return {};

            const result = await createResetPasswordRequest(
              email,
              recaptchaToken
            );

            // The case where it was triggered from the Login Dialog
            if (passwordForgotten && !isFlutterApp) {
              setPasswordForgotten(false);
            }

            if (!remainOpened) {
              // If it was triggered from the drawer
              hideWallet();
              hideDrawer();
            }

            if (result.error) {
              showNotification('passwordForgottenError');
              return { error: 'unable to request password reset' };
            }

            showNotification('passwordForgottenSuccess');
            if (currentUser && !remainOpened) {
              await logOut();
            }

            if (isFlutterApp) {
              postMessage('logout', { isExplicit: true });
            }
            return {};
          };
          if (connectionRecaptcha.current) {
            return processRecaptchaRef(connectionRecaptcha.current);
          }
          if (walletDrawerRecaptcha.current) {
            return processRecaptchaRef(walletDrawerRecaptcha.current);
          }
          sendSafeError(
            'Trying to trigger a password forgotten procedure without a recaptcha context.'
          );
          return processRecaptchaRef(null);
        }
      ),
    [
      createResetPasswordRequest,
      registerHandler,
      showNotification,
      setPasswordForgotten,
      passwordForgotten,
      hideWallet,
      hideDrawer,
      logOut,
      currentUser,
      connectionRecaptcha,
      walletDrawerRecaptcha,
      isFlutterApp,
      postMessage,
    ]
  );
};
